import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Search, LocateFixed, Clock } from "lucide-react";
import { getSavedLocation, setSavedLocation } from "@/lib/cities";

const API = process.env.REACT_APP_BACKEND_URL;

// Compact timezone abbreviation, e.g. "British Summer Time" -> "BST".
// Falls back to a UTC offset label when no name is available.
function tzAbbr(name, tz) {
  if (name) {
    const initials = name.split(/\s+/).filter((w) => /^[A-Z]/.test(w)).map((w) => w[0]).join("");
    if (initials.length >= 2) return initials;
  }
  if (typeof tz === "number") {
    const sign = tz >= 0 ? "+" : "-";
    const h = Math.floor(Math.abs(tz) / 60);
    const m = Math.abs(tz) % 60;
    return `UTC${sign}${h}${m ? ":" + String(m).padStart(2, "0") : ""}`;
  }
  return "";
}

// Live global location autocomplete powered by the Google Maps Geocoding API
// (proxied server-side). On select it saves {name, lat, lon} and calls onChange()
// so the Panchangam refetches for the new coordinates with the selected date/time.
export default function LocationPicker({ onChange }) {
  const saved = getSavedLocation();
  const [label, setLabel] = useState(saved?.name || "New Delhi");
  const [tzInfo, setTzInfo] = useState({
    tz_id: saved?.tz_id || "Asia/Kolkata",
    tz_name: saved?.tz_name || "India Standard Time",
    tz: saved?.tz ?? 330,
  });
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [geoErr, setGeoErr] = useState("");
  const boxRef = useRef(null);
  const timer = useRef(null);
  const abortRef = useRef(null);

  // Debounced (~300ms) Google geocoding search while the dropdown is open.
  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    const query = q.trim();
    if (query.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        const bias = (saved?.lat != null && saved?.lon != null) ? `&lat=${saved.lat}&lon=${saved.lon}` : "";
        const url = `${API}/api/geo/search?q=${encodeURIComponent(query)}${bias}`;
        const res = await fetch(url, { signal: abortRef.current.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setResults(Array.isArray(json) ? json : []);
      } catch (e) {
        if (e.name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, open]);

  // Close on outside click / Escape.
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);

  const apply = async (name, lat, lon, place_id = "") => {
    // Pull the exact timezone (DST-aware) for the chosen coordinates so muhurta
    // timings are computed/shown in that city's local time — never the browser's.
    let tz = 330, tz_id = "Asia/Kolkata", tz_name = "India Standard Time";
    try {
      const res = await fetch(`${API}/api/geo/timezone?lat=${lat}&lon=${lon}`);
      if (res.ok) {
        const j = await res.json();
        if (typeof j.tz === "number") { tz = j.tz; tz_id = j.tz_id || tz_id; tz_name = j.tz_name || tz_name; }
      }
    } catch { /* fall back to IST */ }
    setSavedLocation({ name, lat, lon, place_id, tz, tz_id, tz_name });
    setLabel(name);
    setTzInfo({ tz, tz_id, tz_name });
    setOpen(false);
    setQ("");
    setResults([]);
    onChange && onChange();
  };

  const select = async (r) => {
    const name = r.name || (r.label || "").split(",")[0] || "Selected location";
    // Google Places predictions carry no coordinates — resolve them on select.
    if (r.lat != null && r.lon != null) { apply(name, parseFloat(r.lat), parseFloat(r.lon), r.place_id || ""); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/geo/place?place_id=${encodeURIComponent(r.place_id)}`);
      const j = res.ok ? await res.json() : {};
      if (j.lat != null) { apply(j.name || name, j.lat, j.lon, j.place_id || r.place_id || ""); return; }
      setGeoErr("Could not resolve that location — try another.");
    } catch {
      setGeoErr("Could not resolve that location — try another.");
    } finally {
      setLoading(false);
    }
  };

  // Native GPS: grab the device's exact coordinates, reverse-geocode for a nice
  // label, and update the payload immediately (bypasses the text search). If GPS
  // is unavailable or denied, ask the viewer to search their city.
  const detect = () => {
    setGeoErr("");
    setLocating(true);
    if (!("geolocation" in navigator)) {
      setLocating(false);
      setGeoErr("Could not detect your location — please search your city.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        let name = "My location", place_id = "";
        try {
          const res = await fetch(`${API}/api/geo/reverse?lat=${lat}&lon=${lon}`);
          if (res.ok) { const j = await res.json(); name = j.name || name; place_id = j.place_id || ""; }
        } catch { /* keep default label */ }
        setLocating(false);
        apply(name, lat, lon, place_id);
      },
      () => {
        // GPS denied/unavailable → ask for a city. No IP fallback: behind the CDN it
        // resolves the edge server, not the viewer, and was saved as their location.
        setLocating(false);
        setGeoErr("Location access unavailable — please search your city.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  return (
    <div ref={boxRef} className="relative" data-testid="location-picker">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <button
          type="button"
          onClick={() => { setOpen((o) => !o); setQ(""); setResults([]); setGeoErr(""); }}
          data-testid="location-trigger"
          aria-expanded={open}
          className="inline-flex items-center gap-2 border border-line bg-parchment px-4 py-2.5 text-sm text-ink transition-colors hover:border-[var(--saffron)] focus:border-saffron focus:outline-none"
        >
          <MapPin className="h-4 w-4 text-gold" />
          <span className="max-w-[11rem] truncate">{label}</span>
        </button>
        {tzInfo.tz_id && (
          <span
            data-testid="location-tz-badge"
            title={`${tzInfo.tz_name || tzInfo.tz_id} — times shown in local time`}
            className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] text-ink-3"
          >
            <Clock className="h-3 w-3 text-gold" />
            <span className="font-medium text-ink-2">{tzAbbr(tzInfo.tz_name, tzInfo.tz)}</span>
            <span className="text-ink-3">· {tzInfo.tz_id}</span>
          </span>
        )}
      </div>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-80 max-w-[85vw] border border-line bg-surface shadow-[0_18px_50px_rgba(0,0,0,0.28)]" data-testid="location-dropdown">
          <div className="flex items-center gap-2 border-b border-line px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-ink-3" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search any city worldwide…"
              data-testid="location-search"
              className="w-full bg-transparent py-1 text-sm text-ink placeholder:text-ink-3 focus:outline-none"
            />
            {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gold" />}
          </div>

          {/* Detect My Location (native GPS) */}
          <button
            type="button"
            onClick={detect}
            disabled={locating}
            data-testid="location-detect"
            className="flex w-full items-center gap-2 border-b border-line px-3 py-2.5 text-left text-sm text-[var(--saffron)] transition-colors hover:bg-[var(--surface-alt)] disabled:opacity-60"
          >
            {locating ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <LocateFixed className="h-4 w-4 shrink-0" />}
            {locating ? "Detecting your location…" : "Detect my location"}
          </button>
          {geoErr && <p className="px-3 py-2 text-xs text-red-600" data-testid="location-geo-error">{geoErr}</p>}

          <ul className="max-h-64 overflow-y-auto py-1">
            {q.trim().length < 2 ? (
              <li className="px-3 py-3 text-xs text-ink-3">Type at least 2 letters, or use “Detect my location”.</li>
            ) : loading && results.length === 0 ? (
              <li className="px-3 py-3 text-xs text-ink-3">Searching…</li>
            ) : results.length === 0 ? (
              <li className="px-3 py-3 text-xs text-ink-3">No matches. Try a different spelling.</li>
            ) : (
              results.map((r) => (
                <li key={r.place_id || `${r.lat},${r.lon}`}>
                  <button
                    type="button"
                    onClick={() => select(r)}
                    data-testid="location-option"
                    className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm text-ink-2 transition-colors hover:bg-[var(--surface-alt)] hover:text-[var(--ink)]"
                  >
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
                    <span className="leading-snug">{r.label || r.name}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
